import React, { useEffect, useState } from "react";
import type { ComponentType, FormEventHandler } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field as FieldBase, Formik as FormikBase } from "formik";
import NavigationPromptBase from "@/components/NavigationPrompt";
import { Button, Icon, InputAdornment } from "@mui/material";
import { Cancel as CancelIconBase } from "@mui/icons-material";
import ImageRPBase from "@/components/ImageRP";
import { validateProfile } from "@/helpers/validations";
import { getProfileFormInitialValues } from "../helpers/profileFormValues";
import { authLoadingSelector as isLoadingSelector } from "@/features/auth/store/authSlice";
import {
  isUserUpdatedSelector,
  userSelector,
} from "@/features/profile/store/userSlice";
import {
  getUserThunk,
  updateUserThunk,
} from "@/features/profile/store/userSlice";
import { Fragment } from "react";
import { getBase64 } from "@/helpers/utils";
import {
  imageUploadFieldFormik,
  phoneMDInputFormik,
  textMDInputFormik,
} from "@/helpers/formInputs";
import RpMUIDialog from "@/components/modals/RpMUIModal";
import DialogActions from "@mui/material/DialogActions";
import { USER_ROLE } from "@/config/constants";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

type ContextId = string | number;

type ProfileUser = {
  email?: string;
  first_name?: string;
  fk_role_id?: ContextId;
  id?: ContextId;
  last_name?: string;
  logedInUser?: ContextId;
  logo_image?: File | null;
  logo_name?: string;
  mobile_no?: string;
  profile_image?: File | null;
  profile_name?: string;
  [key: string]: unknown;
};

type ProfileEditProps = {
  brand?: { brand_name?: string; [key: string]: unknown };
  getBrandProfileDetails?: () => void;
  loader: (_loading: boolean) => void;
  loggedInUser: ProfileUser;
  loggedInUserRole: string;
  selectedBrand?: { id?: ContextId; [key: string]: unknown } | null;
  setEdit: (_isEditing: boolean) => void;
  setShowEditProfileModal?: (_show: boolean) => void;
};

type FormikRenderProps = {
  dirty: boolean;
  handleSubmit: FormEventHandler<HTMLFormElement>;
  isSubmitting: boolean;
  setFieldTouched: (_field: string, _isTouched: boolean) => void;
  setFieldValue: (_field: string, _value: unknown) => void;
  values: ProfileUser;
};

type NavigationPromptRenderProps = {
  onCancel: () => void;
  onConfirm: () => void;
  show: boolean;
};

type AppDispatch = (_action: unknown) => Promise<unknown> | unknown;

const Field = FieldBase as ComponentType<any>;
const Formik = FormikBase as ComponentType<any>;
const ImageRP = ImageRPBase as ComponentType<any>;
const NavigationPrompt = NavigationPromptBase as ComponentType<any>;
const RpSvgIcon = RpSvgIconBase as ComponentType<any>;
const CancelIcon = CancelIconBase as ComponentType<any>;

const ProfileEdit = ({
  loggedInUser,
  loggedInUserRole,
  setEdit,
  loader,
  brand = {},
  selectedBrand,
  setShowEditProfileModal = () => {},
  getBrandProfileDetails = () => {},
}: ProfileEditProps) => {
  const dispatch = useDispatch() as AppDispatch;
  const user = useSelector(userSelector) as ProfileUser;
  const isLoading = useSelector(isLoadingSelector) as boolean;
  const isUserUpdated = useSelector(isUserUpdatedSelector) as boolean;
  const initialValues = getProfileFormInitialValues(user);
  /** Image preview state */
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [logoImagePreview, setLogoImagePreview] = useState<string | null>(null);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  useEffect(() => {
    if (isUserUpdated) {
      dispatch(getUserThunk({ ...loggedInUser, brandId: selectedBrand?.id }));
      setEdit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserUpdated]);

  /**
   * @method handleOnChangeImage
   * @description on change image input update imagePreview state to show image preview
   * @param {*} file
   */
  const handleOnChangeImage = async (file?: File | null) => {
    if (!file) {
      setImagePreview(null);
      return;
    }
    const base64Image = (await getBase64(file)) as string;
    setImagePreview(base64Image);
  };

  /**
   * @method handleOnChangeLogoImage
   * @description on change image input update imagePreview state to show image preview
   * @param {*} file
   */
  const handleOnChangeLogoImage = async (file?: File | null) => {
    if (!file) {
      setLogoImagePreview(null);
      return;
    }
    const base64Image = (await getBase64(file)) as string;
    setLogoImagePreview(base64Image);
  };

  /**
   * @method onSubmitForm
   * @description handle on submit
   * @param {*} values
   */
  const onSubmitForm = async (values: ProfileUser) => {
    if (shouldBlockUnsafeSubmission(values)) {
      return;
    }

    let userData = {
      id: loggedInUser.logedInUser ? loggedInUser.logedInUser : loggedInUser.id,
      first_name: values.first_name?.trim() || "",
      last_name: values.last_name?.trim() || "",
      mobile_no: values.mobile_no?.replace(/-/g, "") || "",
      fk_role_id: values.fk_role_id,
      status: 1,
      is_deleted: 0,
      profile_name: values.profile_name,
      profile_base: "",
      logo_name: values.logo_name,
      logo_base: "",
      brandId: selectedBrand?.id,
      password: "",
    };

    if (values.profile_image) {
      const imageName = values.profile_image.name.split(".");
      imageName.pop();
      userData = {
        ...userData,
        profile_name: imageName.join(),
        profile_base: (await getBase64(values.profile_image)) as string,
      };
    }

    if (values.logo_image) {
      const imageName = values.logo_image.name.split(".");
      imageName.pop();
      userData = {
        ...userData,
        logo_name: imageName.join(),
        logo_base: (await getBase64(values.logo_image)) as string,
      };
    }

    await dispatch(updateUserThunk(userData));
    setShowEditProfileModal(false);
    getBrandProfileDetails();
  };
  return (
    <Formik
      initialValues={initialValues || {}}
      enableReinitialize={true}
      onSubmit={onSubmitForm}
      validate={validateProfile}
    >
      {({
        handleSubmit,
        values,
        isSubmitting,
        dirty,
        setFieldValue,
        setFieldTouched,
      }: FormikRenderProps) => (
        <Fragment>
          <form onSubmit={handleSubmit}>
            <div
              className={`card profile-card ${loggedInUserRole === USER_ROLE.PARTICIPANT.NAME ? "profile-card-border" : ""}`}
            >
              <div className="card-header profile-card-header">
                <div className="profile-image-box w-100">
                  <div className="profile-image-container">
                    <ImageRP
                      className="profile-image"
                      src={imagePreview ? imagePreview : values.profile_name}
                      type={imagePreview ? "BASE64" : "PROFILE"}
                    />
                    {imagePreview && (
                      <div className="cancel-icon-box">
                        <CancelIcon
                          onClick={() => {
                            setTimeout(() => {
                              setFieldValue("profile_image", "");
                              setFieldTouched("profile_image", false);
                            }, 100);
                            setImagePreview(null);
                          }}
                          className="close-icon"
                          title="Remove"
                        />
                      </div>
                    )}
                  </div>
                  <Field
                    name="profile_image"
                    component={imageUploadFieldFormik}
                    onChangeImage={handleOnChangeImage}
                    errorClass="text-center"
                    hint="Max 2MB only"
                  />
                </div>
              </div>

              <div className="card-body text-center profile-content">
                {/* {loggedInUserRole === "PR" && (
                  <div className="user-id-box mb-2">
                    <div className="id">
                      {`Unique ID : ${loggedInUser?.participantData?.system_generated_participant_id}`}
                    </div>
                  </div>
                )} */}
                {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
                  <div className="mb-3">
                    {brand?.brand_name ? brand?.brand_name : ""}
                  </div>
                )}
                <h2 className="user-name mb-0">
                  <Field
                    label="First Name"
                    name="first_name"
                    component={textMDInputFormik}
                    className="user-name"
                    formGroupClass="mb-0 py-2"
                    size="small"
                  />
                  <Field
                    label="Last Name"
                    name="last_name"
                    component={textMDInputFormik}
                    className="user-name"
                    formGroupClass="mb-0 py-2"
                    size="small"
                  />
                </h2>
                <div className="user-email mb-0">
                  <Field
                    label="Email"
                    name="email"
                    component={textMDInputFormik}
                    formGroupClass="py-2 mb-0 w-100"
                    disabled={true}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon
                            className="icon"
                            fontSize="small"
                            component={RpSvgIcon}
                            name="envelope_icon"
                            color="primary"
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <div className="user-phone mb-0">
                  <Field
                    label="Phone"
                    name="mobile_no"
                    component={phoneMDInputFormik}
                    formGroupClass="py-2 mb-0 w-100"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon
                            className="icon"
                            fontSize="small"
                            component={RpSvgIcon}
                            name="handset_icon"
                            color="primary"
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
                  <div className="brand-logo-box">
                    <h6 className="brand-logo-title">
                      {loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
                      loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
                      loggedInUserRole ===
                        USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
                        ? "Company Logo"
                        : "Brand Logo"}
                    </h6>
                    <div className="brand-logo-container">
                      <ImageRP
                        className="brand-logo"
                        src={
                          logoImagePreview ? logoImagePreview : values.logo_name
                        }
                        type={
                          logoImagePreview
                            ? "BASE64"
                            : loggedInUserRole ===
                                  USER_ROLE.PARENT_COMPANY.NAME ||
                                loggedInUserRole ===
                                  USER_ROLE.PARENT_COMPANY_USER.NAME ||
                                loggedInUserRole ===
                                  USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
                              ? "COMPANY"
                              : "BRAND"
                        }
                      />
                      {logoImagePreview && (
                        <div className="cancel-icon-box">
                          <CancelIcon
                            onClick={() => {
                              setTimeout(() => {
                                setFieldValue("logo_image", "");
                                setFieldTouched("logo_image", false);
                              }, 100);
                              setLogoImagePreview(null);
                            }}
                            className="close-icon"
                            title="Remove"
                          />
                        </div>
                      )}
                    </div>
                    <Field
                      name="logo_image"
                      component={imageUploadFieldFormik}
                      onChangeImage={handleOnChangeLogoImage}
                      errorClass="my-0 text-center"
                      hintClass="mt-0"
                      hint="Max 2MB only"
                      disabled={loggedInUserRole !== USER_ROLE.BRAND_OWNER.NAME}
                    />
                  </div>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!dirty || isSubmitting}
                  fullWidth
                  className="action-btn mt-2"
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setEdit(false);
                    setShowEditProfileModal(false);
                  }}
                  disabled={isSubmitting}
                  fullWidth
                  className="action-btn mt-2"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
          <NavigationPrompt when={dirty && !isUserUpdated}>
            {({ onConfirm, onCancel, show }: NavigationPromptRenderProps) => (
              <RpMUIDialog open={show} onClose={onCancel} maxWidth="sm">
                <h4 className="heading61 text-center">
                  Changes made will be lost.
                </h4>
                <DialogActions
                  sx={{
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onCancel}
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                    size="small"
                  >
                    Leave Anyway
                  </Button>
                </DialogActions>
              </RpMUIDialog>
            )}
          </NavigationPrompt>
        </Fragment>
      )}
    </Formik>
  );
};

export default ProfileEdit;
